import axios from "axios";
import { createLogger } from "debug/DebugingLogger";
import { useAppInitializationStore } from "./appInitializationStore";

const log = createLogger("fetchWithInit");

type InitFlag = "modelsInitialized" | "teamsInitialized" | "tokensInitialized";

export interface FetchListOptions {
  showLoading?: boolean;
  refresh?: boolean;
  /** The response must postdate this call — a fetch already in flight may
   *  carry pre-mutation data, so it cannot satisfy a `force` request. Used
   *  after mutations (restore, dissociate); the poll stays plain `refresh`. */
  force?: boolean;
}

// Dedup concurrent fetches: if a fetch is already in-flight for a given key,
// subsequent callers get the same promise instead of firing a new request.
const inFlight = new Map<string, Promise<unknown[]>>();

/**
 * Shared fetch-with-initialization logic for main stores.
 * Handles caching guard, loading state, error state, and init flag.
 */
export async function fetchWithInit<T>(
  set: (partial: Record<string, unknown>) => void,
  options: FetchListOptions | undefined,
  config: {
    initFlag: InitFlag;
    dataKey: string;
    cachedData: T[];
    fetchFn: () => Promise<T[]>;
    setData?: (data: T[]) => void;
    errorMessage: string;
  },
): Promise<T[]> {
  const { showLoading = false, refresh = false, force = false } = options || {};
  const initStore = useAppInitializationStore.getState();
  const isInitialized = initStore[config.initFlag];

  if (isInitialized && !refresh && !force && config.cachedData.length > 0)
    return config.cachedData;

  const existing = inFlight.get(config.initFlag);
  if (existing) {
    if (!force) return existing as Promise<T[]>;
    // Chain a fresh fetch behind the in-flight one instead of racing it.
    return (existing as Promise<T[]>).then(() =>
      fetchWithInit(set, { ...options, force: false, refresh: true }, config),
    );
  }

  if (showLoading || !isInitialized) set({ isLoading: true, error: null });
  else if (refresh) set({ error: null });

  const promise = (async () => {
    try {
      const fetched = await config.fetchFn();
      if (config.setData) {
        config.setData(fetched);
      } else {
        set({ [config.dataKey]: fetched });
      }
      setInitFlag(config.initFlag);
      return fetched;
    } catch (error) {
      if (axios.isCancel(error) || !navigator.onLine) {
        return config.cachedData;
      }
      log.error(config.errorMessage, error);
      set({
        [config.dataKey]: [],
        error: `${config.errorMessage}. Please try again.`,
      });
      return [];
    } finally {
      set({ isLoading: false });
      inFlight.delete(config.initFlag);
    }
  })();

  inFlight.set(config.initFlag, promise);
  return promise as Promise<T[]>;
}

function setInitFlag(flag: InitFlag): void {
  const store = useAppInitializationStore.getState();
  if (flag === "modelsInitialized") store.setModelsInitialized(true);
  else if (flag === "teamsInitialized") store.setTeamsInitialized(true);
  else store.setTokensInitialized(true);
}
